import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useApi } from "../api/useApi";
import { fetchCredits } from "../api/resources";

/** The signed-in account's premium credits, in the header on every page (owner, 2026-10-27: "it should be simpler for
 * me to see premium token count"). Read again on each page, so a credit spent or bought shows. An admin has premium
 * without credits. Nothing shows while it loads or when the read fails: never a broken header. */
export default function PremiumCreditsChip({ isAdmin }: { isAdmin: boolean }) {
  return isAdmin ? <Chip label="Premium: unlimited" short="∞" /> : <CreditCount />;
}

function CreditCount() {
  const { pathname } = useLocation();
  const apiFetch = useApi();
  // A ref, not a dependency: re-reading on apiFetch's identity made a render loop wherever useApi is not memoised.
  const apiRef = useRef(apiFetch);
  apiRef.current = apiFetch;
  const [available, setAvailable] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchCredits(apiRef.current).then(
      (c) => { if (!cancelled) setAvailable(Number(c?.available) || 0); },
      () => { if (!cancelled) setAvailable(null); }
    );
    return () => { cancelled = true; };
  }, [pathname]);

  if (available == null) return null;
  return <Chip label={`${available} premium ${available === 1 ? "credit" : "credits"}`} short={String(available)} />;
}

/** The whole label from sm up; on a phone's header only the icon and the number (the label stays its name). */
function Chip({ label, short }: { label: string; short: string }) {
  return (
    <span
      data-testid="premium-credits"
      title={label}
      aria-label={label}
      className="inline-flex items-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium shrink-0"
    >
      <Sparkles className="h-4 w-4" aria-hidden="true" />
      <span className="sm:hidden" aria-hidden="true">{short}</span>
      <span className="hidden sm:inline" aria-hidden="true">{label}</span>
    </span>
  );
}
